import { getLogger } from "../../shared/logger";
import { _getVersion } from "./metadata";
/**
 * MetadataModule provides SDK version information and health monitoring capabilities.
 *
 * Offers access to SDK version details, service health checks, and metadata
 * about supported blockchain networks and tokens.
 *
 * **Key Features:**
 * - SDK version information and compatibility checks
 * - Service health monitoring and diagnostics
 * - Chain and token metadata access
 * - Service discovery and endpoint information
 *
 * @example
 * ```typescript
 * const metadata = new MetadataModule({
 *   bdApiKey: "your-blockdaemon-api-key"
 * });
 *
 * // Get SDK version
 * const version = metadata.getVersion();
 * console.log(`BD SDK Version: ${version}`);
 *
 * // Check service health
 * const health = await metadata.getHealth();
 * console.log("Service is healthy:", health);
 * ```
 *
 * @public
 */
export class MetadataModule {
    logger;
    bdApiKey;
    options;
    /**
     * Creates a new MetadataModule instance.
     *
     * @param options - Configuration options for the metadata module
     * @throws {Error} If the BD API key is missing or empty
     *
     * @example
     * ```typescript
     * const metadata = new MetadataModule({
     *   bdApiKey: process.env.BD_API_KEY,
     *   logger: customLogger // optional
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Basic initialization
     * const metadata = new MetadataModule({
     *   bdApiKey: "your-blockdaemon-api-key"
     * });
     * ```
     */
    constructor(options) {
        this.logger = options.logger || getLogger({ level: "info", label: "bd-sdk-metadata-module" });
        if (!options.bdApiKey || options.bdApiKey.trim() === "") {
            this.logger.warn("BD API key is not set. Some metadata operations may fail with 401 Unauthorized.");
            this.bdApiKey = undefined;
        }
        else {
            this.bdApiKey = options.bdApiKey;
        }
        this.options = options;
    }
    /**
     * Retrieves the current SDK version information.
     *
     * @remarks
     * This method provides the version string of the currently running BD SDK.
     * It's useful for version validation, compatibility checks, and debugging.
     * The version follows semantic versioning (semver) standards.
     *
     * @returns The current SDK version string in semver format
     * @throws {Error} If the SDK configuration is not initialized
     *
     * @example
     * ```typescript
     * const version = metadata.getVersion();
     * console.log(`Current SDK version: ${version}`);
     * ```
     *
     * @example
     * **Version Compatibility Check**
     * ```typescript
     * const currentVersion = metadata.getVersion();
     * const minimumVersion = "1.0.0";
     *
     * // Simple version comparison (for production use a proper semver library)
     * if (currentVersion < minimumVersion) {
     *   throw new Error(`SDK version ${currentVersion} is below minimum required ${minimumVersion}`);
     * }
     * ```
     *
     * @example
     * **Application Startup Validation**
     * ```typescript
     * console.log(`Starting application with BD SDK v${metadata.getVersion()}`);
     *
     * // Log version for debugging and support
     * const appInfo = {
     *   appVersion: process.env.APP_VERSION,
     *   sdkVersion: metadata.getVersion(),
     *   nodeVersion: process.version,
     *   platform: process.platform
     * };
     *
     * console.log("Application info:", appInfo);
     * ```
     *
     */
    getVersion() {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return _getVersion();
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoibWV0YWRhdGEtbW9kdWxlLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vLi4vc3JjL21haW4vbW9kdWxlcy9tZXRhZGF0YS9tZXRhZGF0YS1tb2R1bGUudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQ0EsT0FBTyxFQUFFLFNBQVMsRUFBRSxNQUFNLHFCQUFxQixDQUFDO0FBQ2hELE9BQU8sRUFBRSxXQUFXLEVBQUUsTUFBTSxZQUFZLENBQUM7QUFHekM7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0E0Qkc7QUFDSCxNQUFNLE9BQU8sY0FBYztJQUNSLE1BQU0sQ0FBUztJQUNmLFFBQVEsQ0FBcUI7SUFDdEMsT0FBTyxDQUF5QjtJQUV4Qzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O09BcUJHO0lBQ0gsWUFBWSxPQUErQjtRQUN6QyxJQUFJLENBQUMsTUFBTSxHQUFHLE9BQU8sQ0FBQyxNQUFNLElBQUksU0FBUyxDQUFDLEVBQUUsS0FBSyxFQUFFLE1BQU0sRUFBRSxLQUFLLEVBQUUsd0JBQXdCLEVBQUUsQ0FBQyxDQUFDO1FBQzlGLElBQUksQ0FBQyxPQUFPLENBQUMsUUFBUSxJQUFJLE9BQU8sQ0FBQyxRQUFRLENBQUMsSUFBSSxFQUFFLEtBQUssRUFBRSxFQUFFLENBQUM7WUFDeEQsSUFBSSxDQUFDLE1BQU0sQ0FBQyxJQUFJLENBQ2QsaUZBQWlGLENBQ2xGLENBQUM7WUFDRixJQUFJLENBQUMsUUFBUSxHQUFHLFNBQVMsQ0FBQztRQUM1QixDQUFDO2FBQU0sQ0FBQztZQUNOLElBQUksQ0FBQyxRQUFRLEdBQUcsT0FBTyxDQUFDLFFBQVEsQ0FBQztRQUNuQyxDQUFDO1FBQ0QsSUFBSSxDQUFDLE9BQU8sR0FBRyxPQUFPLENBQUM7SUFDekIsQ0FBQztJQUVEOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7T0E2Q0c7SUFDSSxVQUFVO1FBQ2YsSUFBSSxDQUFDLElBQUksQ0FBQyxPQUFPO1lBQUUsTUFBTSxJQUFJLEtBQUssQ0FBQyw0QkFBNEIsQ0FBQyxDQUFDO1FBQ2pFLE9BQU8sV0FBVyxFQUFFLENBQUM7SUFDdkIsQ0FBQztDQXFPRiJ9